import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import styled from '@emotion/styled';
import { ChatMarkdown } from '@trinity/chat-components/components/Markdown/Markdown.js';
import type { BaseModalProps } from '@trinity/chat-components/hooks/useModal.js';

export type SourceInfo = {
  sourceId: string;
  content: string;
  citationLabel?: string;
  transcript?: {
    id: number;
    title: string;
    publishDate?: Date | string | null;
  } | null;
  tags?: string[];
  score?: number;
};

type Props = BaseModalProps & {
  source: SourceInfo | null;
};

export const SourceCitationModal = ({ open, onClose, source }: Props) => {
  if (!source) return null;

  return (
    <Dialog fullWidth maxWidth="sm" onClose={onClose} open={open}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {source.citationLabel ? `Source ${source.citationLabel}` : 'Source'}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {source.transcript && (
          <TranscriptInfo>
            <TranscriptTitle>{source.transcript.title}</TranscriptTitle>
          </TranscriptInfo>
        )}
        <SourceContent>
          <ChatMarkdown value={source.content} />
        </SourceContent>
      </DialogContent>
    </Dialog>
  );
};

SourceCitationModal.displayName = 'Transcript.Modal.SourceCitation';

const TranscriptInfo = styled.div`
  margin-bottom: 12px;
`;

const TranscriptTitle = styled.div`
  font-family: var(--font-semibold);
  font-size: 14px;
  color: var(--black-l);
`;

const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
`;

const Tag = styled.span`
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  background: var(--gray-xl);
  color: var(--gray-d);
`;

const SourceContent = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: var(--black-l);
  white-space: pre-wrap;
`;
